/**
 * Participant-side data layer. Mirrors the web join page's contract exactly:
 *   1. Mint a participant token (POST /api/sessions/:id/participant-tokens)
 *   2. Fetch the active activity via the per-type "active" endpoints
 *   3. Submit via the per-type response endpoints, Bearer <participantToken>
 *
 * NOTE: this replaces the old generic submitResponse() which posted to
 * /api/sessions/:sid/nodes/:nodeId/responses — an endpoint that never
 * existed on the backend (participant submits from Zoom always 404'd).
 */

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:4000';

/* ------------------------------------------------------------------ */
/*  Participant identity                                               */
/* ------------------------------------------------------------------ */

let participantToken: string | null = null;
let participantId: string | null = null;
let participantName: string | undefined;

export function getParticipantId(): string | null {
  return participantId;
}

async function pFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (participantToken) headers['Authorization'] = `Bearer ${participantToken}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Attach the HTTP status so callers can branch (e.g. 409 = already
    // answered) instead of string-matching the message.
    const err = new Error(body.error || `API ${res.status}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/**
 * Mint (or re-mint) a participant token for this session. Idempotent per
 * session — call once after auth; submits then carry the Bearer token.
 */
export async function ensureParticipant(
  sessionId: string,
  displayName?: string,
): Promise<void> {
  if (participantToken && participantId) return;
  const res = await fetch(`${API_BASE}/api/sessions/${sessionId}/participant-tokens`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(displayName ? { displayName } : {}),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Could not join session (${res.status})`);
  }
  const data = await res.json();
  participantToken = data.participantToken;
  participantId = data.participantId;
  participantName = displayName;
}

export function clearParticipant(): void {
  participantToken = null;
  participantId = null;
  participantName = undefined;
}

/* ------------------------------------------------------------------ */
/*  Activity DTOs (subset of the web DTOs that the sidebar renders)    */
/* ------------------------------------------------------------------ */

export interface ActiveQuestion {
  _id: string;
  promptText: string;
}

export interface ActivePoll {
  _id: string;
  question: string;
  options: Array<{ id: string; text: string; color?: string }>;
  allowMultiple: boolean;
}

export interface ActiveQuiz {
  _id: string;
  title: string;
  questions: Array<{
    promptText: string;
    options: Array<{ id?: string; _id?: string; text: string }>;
    correctOptionId?: string;
  }>;
  currentQuestionIndex: number;
  /** True once the facilitator has revealed the current question's answer. */
  revealedAnswer?: boolean;
}

export interface ActiveNps {
  _id: string;
  question: string;
}

export interface ActiveWordCloud {
  _id: string;
  text: string;
  maxSubmissions: number;
}

export interface ActiveSwot {
  _id: string;
  title: string;
  frameworkType?: string;
}

export interface ActiveExam {
  _id: string;
  title: string;
  showResultsToParticipants?: boolean;
  questions: {
    promptText: string;
    options: { id?: string; _id?: string; text: string }[];
  }[];
}

export interface ExamSubmitResult {
  isCorrect: boolean;
  finished: boolean;
  /** Only present on the final answer when showResultsToParticipants is true */
  score?: number;
  total?: number;
}

/* ------------------------------------------------------------------ */
/*  Active-activity fetchers (same endpoints as the web join page)     */
/* ------------------------------------------------------------------ */

export const getActiveQuestion = (sessionId: string) =>
  pFetch<ActiveQuestion | null>(`/api/sessions/${sessionId}/active-question`);

export const getActivePoll = (sessionId: string) =>
  pFetch<ActivePoll | null>(`/api/polls/session/${sessionId}/active`);

export const getActiveQuiz = (sessionId: string) =>
  pFetch<ActiveQuiz | null>(`/api/quizzes/session/${sessionId}/active`);

export const getActiveNps = (sessionId: string) =>
  pFetch<ActiveNps | null>(`/api/nps/session/${sessionId}/active`);

export const getActiveWordCloud = (sessionId: string) =>
  pFetch<ActiveWordCloud | null>(`/api/word-clouds/session/${sessionId}/active`);

export const getActiveSwot = (sessionId: string) =>
  pFetch<ActiveSwot | null>(`/api/swot-analyses/session/${sessionId}/active`);

export const getActiveExam = (sessionId: string) =>
  pFetch<ActiveExam | null>(`/api/exams/session/${sessionId}/active`);

/* ------------------------------------------------------------------ */
/*  Aggregated RESULTS (for the in-app Collaborate display)            */
/*  Same endpoints + shapes the web display page reads. The participant */
/*  token (minted via ensureParticipant) authenticates these reads.    */
/* ------------------------------------------------------------------ */

export interface PollResults {
  pollId: string;
  question: string;
  options: Array<{ id: string; text: string; color: string; votes: number; percentage: number }>;
  totalVotes: number;
}

export interface NpsResults {
  npsId: string;
  totalResponses: number;
  detractors: number;
  passives: number;
  promoters: number;
  detractorPct: number;
  passivePct: number;
  promoterPct: number;
  npsScore: number;
}

export interface WordCloudResults {
  wordCloudId: string;
  text: string;
  words: Array<{ word: string; count: number }>;
  totalResponses: number;
  totalWords: number;
}

export interface QuestionResponse {
  _id: string;
  participantName?: string;
  bodyText?: string;
  createdAt: string;
}

export const getPollResults = (pollId: string) =>
  pFetch<PollResults>(`/api/polls/${pollId}/results`);

export const getNpsResults = (npsId: string) =>
  pFetch<NpsResults>(`/api/nps/${npsId}/results`);

export const getWordCloudResults = (id: string) =>
  pFetch<WordCloudResults>(`/api/word-clouds/${id}/results`);

export const getQuestionResponses = (questionId: string) =>
  pFetch<QuestionResponse[]>(`/api/questions/${questionId}/responses`);

export interface QuestionSummary {
  oneSentenceSummary: string;
  sentiment: string;
  themes: Array<{ theme: string; description: string; exampleQuotes: string[] }>;
  responseCount: number;
}

/** Read the cached AI summary (participant-readable). Null if none generated. */
export const getQuestionSummary = (questionId: string) =>
  pFetch<QuestionSummary | null>(`/api/questions/${questionId}/summary`);

export interface QuizResults {
  quizId: string;
  title: string;
  totalQuestions: number;
  questionResults: Array<{
    questionIndex: number;
    promptText: string;
    totalVotes: number;
    optionResults: Array<{ id: string; text: string; color: string; votes: number; percentage: number }>;
  }>;
  leaderboard: Array<{ rank: number; participantName: string; score: number; total: number }>;
}

export interface SwotResults {
  swotAnalysisId: string;
  title: string;
  frameworkType?: string;
  categories: Record<string, Array<{ _id: string; text: string; participantName?: string }>>;
  totalResponses: number;
  responseCounts: Record<string, number>;
}

export interface ActiveCrossTab {
  _id: string;
}

export interface CrossTabResults {
  crossTabId: string;
  title: string;
  question1: { promptText: string; options: Array<{ id: string; text: string; color: string }> };
  question2: { promptText: string; options: Array<{ id: string; text: string; color: string }> };
  totalResponses: number;
  matrix: Record<string, Record<string, number>>;
}

export const getQuizResults = (quizId: string) =>
  pFetch<QuizResults>(`/api/quizzes/${quizId}/results`);

export const getSwotResults = (id: string) =>
  pFetch<SwotResults>(`/api/swot-analyses/${id}/results`);

export interface SwotSummary {
  categorySummaries: Record<string, Array<{ point: string; count: number }>>;
}

/** Cached AI summary for a SWOT/framework activity (participant-readable). */
export const getSwotSummary = (id: string) =>
  pFetch<SwotSummary | null>(`/api/swot-analyses/${id}/summary`);

export const getActiveCrosstab = (sessionId: string) =>
  pFetch<ActiveCrossTab | null>(`/api/cross-tabs/session/${sessionId}/active`);

export const getCrossTabResults = (id: string) =>
  pFetch<CrossTabResults>(`/api/cross-tabs/${id}/results`);

export interface ActiveImageActivity {
  _id: string;
}

export interface ImageActivityResults {
  imageActivityId: string;
  title: string;
  mode: 'click' | 'dragdrop';
  imagePath?: string;
  totalResponses: number;
  responses: Array<{
    _id: string;
    participantName?: string;
    clickX?: number; // 0-1 normalized
    clickY?: number;
    placements?: Array<{ itemId: string; itemLabel: string; x: number; y: number }>;
  }>;
}

export const getActiveImageActivity = (sessionId: string) =>
  pFetch<ActiveImageActivity | null>(`/api/image-activities/session/${sessionId}/active`);

export const getImageActivityResults = (id: string) =>
  pFetch<ImageActivityResults>(`/api/image-activities/${id}/results`);

/**
 * Build an authorized image URL. The /uploads route is gated by
 * requireSessionRead, and an <img> can't send a Bearer header — so the read
 * token rides as a ?token= query param (same as the web display page). Call
 * after ensureParticipant() so the token exists.
 */
export function getImageActivityImageUrl(imagePath: string): string {
  const base = `${API_BASE}/${imagePath.replace(/^\/+/, '')}`;
  return participantToken
    ? `${base}?token=${encodeURIComponent(participantToken)}`
    : base;
}

/**
 * Fetch a gated upload as an object URL using the Authorization header (the
 * canonical auth path — CORS-allowed for the Zoom origin). More reliable than
 * an <img ?token> request, and surfaces the real HTTP status so a missing file
 * (404 — e.g. a disk-stored image wiped by a redeploy; only GridFS persists)
 * is distinguishable from an auth failure. Caller must URL.revokeObjectURL.
 */
/** Slide full-image URL. The /api/slides/:id/{full-image} route is public
 * (it's how the participant display loads slides), so no token is needed. */
export function getSlideImageUrl(slideId: string): string {
  return `${API_BASE}/api/slides/${slideId}/full-image`;
}

/**
 * Fetch the slide image as an object URL. Mirrors the image-activity approach
 * (blob via fetch → blob: URL) — more reliable than a cross-origin <img> in
 * the Zoom webview, and surfaces the real HTTP status (404 = no image for that
 * slide id). Caller must URL.revokeObjectURL.
 */
export async function fetchSlideImageObjectUrl(slideId: string): Promise<string> {
  const res = await fetch(getSlideImageUrl(slideId));
  if (!res.ok) throw new Error(String(res.status));
  return URL.createObjectURL(await res.blob());
}

export async function fetchUploadObjectUrl(imagePath: string): Promise<string> {
  const url = `${API_BASE}/${imagePath.replace(/^\/+/, '')}`;
  const res = await fetch(url, {
    headers: participantToken ? { Authorization: `Bearer ${participantToken}` } : {},
  });
  if (!res.ok) throw new Error(String(res.status));
  return URL.createObjectURL(await res.blob());
}

/* ------------------------------------------------------------------ */
/*  Submits (same payloads as the web join page)                       */
/* ------------------------------------------------------------------ */

export const submitQuestionResponse = (questionId: string, bodyText: string) =>
  pFetch(`/api/questions/${questionId}/responses`, {
    method: 'POST',
    body: JSON.stringify({
      bodyText,
      ...(participantId && { participantId }),
      ...(participantName && { participantName }),
    }),
  });

export const submitPollResponse = (pollId: string, selectedOptionIds: string[]) =>
  pFetch(`/api/polls/${pollId}/responses`, {
    method: 'POST',
    body: JSON.stringify({
      selectedOptions: selectedOptionIds,
      participantId,
      ...(participantName && { participantName }),
    }),
  });

export const submitQuizResponse = (quizId: string, selectedOptionId: string) =>
  pFetch(`/api/quizzes/${quizId}/responses`, {
    method: 'POST',
    body: JSON.stringify({ selectedOptionId, participantId, participantName }),
  });

export const submitNpsResponse = (npsId: string, score: number) =>
  pFetch(`/api/nps/${npsId}/responses`, {
    method: 'POST',
    body: JSON.stringify({ score, participantId, participantName }),
  });

export const submitWordCloudResponse = (id: string, words: string[]) =>
  pFetch(`/api/word-clouds/${id}/responses`, {
    method: 'POST',
    body: JSON.stringify({ words, participantId, participantName }),
  });

export const submitSwotResponse = (id: string, category: string, text: string) =>
  pFetch(`/api/swot-analyses/${id}/responses`, {
    method: 'POST',
    body: JSON.stringify({ category, text, participantId, participantName }),
  });

export const submitExamResponse = (examId: string, questionIndex: number, selectedOptionId: string) =>
  pFetch<ExamSubmitResult>(`/api/exams/${examId}/responses`, {
    method: 'POST',
    body: JSON.stringify({ questionIndex, selectedOptionId, participantId, participantName }),
  });
